import type { TryOnProvider as TryOnProviderType } from '@zakeke/zakeke-3d-core';
import { BasicButton, PrimaryButton } from '../buttons';
import { HelpIcon } from '../helpIconContainer';
import { Icon } from '../icon';
import { AdvancedSelect } from '../select';
import { showNotification } from '../notificationToaster';
import React, { PropsWithChildren } from 'react';
import ArrowDownSolid from 'src/shared/material_icons/navigation/arrow_downward.svg';
import ArrowLeftSolid from 'src/shared/material_icons/navigation/arrow_back.svg';
import ArrowRightSolid from 'src/shared/material_icons/navigation/arrow_forward.svg';
import ArrowUpSolid from 'src/shared/material_icons/navigation/arrow_upward.svg';
import FullscreenIcon from 'src/shared/material_icons/navigation/fullscreen.svg';
import MinusSolid from 'src/shared/material_icons/content/remove.svg';
import PlusSolid from 'src/shared/material_icons/content/add.svg';
import TimesSolid from 'src/shared/material_icons/navigation/close.svg';

import { tryOnMazingFinetuningEnvironments, tryOnMazingFinetuningTemples } from 'src/components/pages/virtualTryOn/hooks/useVTOProductType';
import config from 'src/shared/config';
import { T, uploadFile } from 'src/shared/helpers';
import styled from 'styled-components';
import DeepAR from '../deepAR/deepAR';

const VtoFinetuningContainerBase = styled.div<{ $isFullScreen: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    position: relative;
    min-height: 630px;
    width: 100%;
    height: 100%;
    ${(props) =>
        props.$isFullScreen &&
        `min-height: 825px;position: absolute; top: 3%; left: 2%; z-index: 1000; width: 96%; height: 100%
    ;`};
    z-index: 51;
`;

const VTOFineTuningContainerOverlay = styled.div<{ $isAbove?: boolean }>`
    position: fixed;
    top: 0px;
    left: 0px;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: ${(props) => (props.$isAbove ? 10000 : 50)};
`;

const VtoFinetuningFrameContainer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2;
`;

const VtoFinetuningFrame = styled.iframe`
    width: 100%;
    height: 100%;
    outline: none;
    border: none;
`;

const VtoFinetuningControlsContainer = styled.div`
    position: absolute;
    bottom: 0px;
    right: 0px;
    width: 200px;
    height: 100%;
    z-index: 3;
    padding-right: 25px;
`;

const VtoFinetuningControlsVariableContainer = styled.div<{ $heightBlock: number }>`
    display: flex;
    flex-direction: column;
    justify-content: end;
    width: 100%;
    height: 12%;
    z-index: 4;
    align-items: flex-end;
    height: ${(props) => props.$heightBlock}%;
`;

const ControlsMovementContainer = styled.div`
    display: flex;
    flex-flow: wrap;
    width: 100%;
    height: 100px;
    justify-content: flex-end;

    & > button {
        width: 55px;
        padding: 10px;
        border-radius: 0;
    }
    & > button:hover {
        background: #f1f2f3 !important;
        border-color: #8fa4ae !important;
    }
`;

const StyledIcon = styled(Icon)`
    width: 24px;
    height: 24px;
    cursor: pointer;
`;

const CenteredFluidContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 150px;
    height: 100%;
    gap: 10px;
    flex-direction: column;
`;

const MovementButton = styled(BasicButton)`
    &:focus {
        color: #f1f2f3;
        border-color: #8fa4ae;
        svg {
            fill: rgb(143, 164, 174);
        }
    }
`;

const ComponentsContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    width: 100%;
    gap: 10px;
`;

interface Props {
    provider: TryOnProviderType;
    model: Blob;
    modelUrl?: string; // URL originale per prodotti DAM (evita upload)
    children?: React.ReactNode;
    currentSettings?: any;
    onLoaded?: () => void;
    onReady?: () => void;
    onSettingsChange?: (settings: any) => void;
    onSettingsSave?: (settings: any) => void;
    onFullScreen?: (value: boolean) => void;
}

enum FinetuningMessageType {
    Start = '',
    WaitingAuthentication = 'WAITING_AUTH',
    AuthSuccessful = 'AUTH_SUCCESS',
    FineTunerLoaded = 'FINE_TUNER_LOADED',
    NewSettings = 'NEW_SETTINGS',
    AuthError = 'AUTH_ERROR',
    WebcamError = 'WEBCAM_ERROR'
}

interface FinetuningMessage {
    type: FinetuningMessageType;
    value?: any;
}

function FormatMazingMessage(type: string, value: any) {
    return { type, value: value } as const;
}

function FormatBanubaMessage(type: string, value: any) {
    return { type, ...value } as const;
}

function FormatDeepARMessage(type: string, value: any) {
    return { type, ...value } as const;
}

const VtoFinetuning = ({
    provider,
    model,
    modelUrl,
    currentSettings,
    children,
    onLoaded,
    onReady,
    onSettingsChange,
    onSettingsSave,
    onFullScreen
}: PropsWithChildren<Props>) => {
    const frame = React.useRef<HTMLIFrameElement>(null);
    const [isLoaded, setIsLoaded] = React.useState(false);
    const [isReady, setIsReady] = React.useState(false);
    const [isFullScreen, setIsFullScreen] = React.useState(false);
    const [providerInfo, setProviderInfo] = React.useState<{ url: string | undefined; auth: string | undefined } | null>(null);
    const [currentProvider, setCurrentProvider] = React.useState<number>(provider);
    const [isCurrentProviderMazing, setIsCurrentProviderMazing] = React.useState(false);
    const [currentSettingsState, setCurrentSettingsState] = React.useState(
        currentSettings === null
            ? {
                  position: {
                      x: 0,
                      y: 0,
                      z: 0
                  },
                  rotation: {
                      x: 0,
                      y: 0,
                      z: 0
                  },
                  scale: 1,
                  environment: 'https://zakekecdn.blob.core.windows.net/cdn/3d/assets/environments/deepAR_HDRs/Warehouse-with-lights_(intermediate)_new.hdr'
              }
            : currentSettings
    );
    console.log('currentSettingsState', currentSettingsState, isLoaded);
    const providerInfoRef = React.useRef(providerInfo);
    providerInfoRef.current = providerInfo;
    const [deepARIframeUrl, setDeepARIframeUrl] = React.useState<string>('');
    const [uploadedFileUrl, setUploadedFileUrl] = React.useState<string>('');

    const getProviderAuth = async () => {
        const { TryOnProvider } = await import('@zakeke/zakeke-3d-core');
        const viewBag = document.getElementById('viewbag');
        console.log('viewBag', viewBag);
        let url = '',
            auth = '';
        if (provider === TryOnProvider.Mazing) {
            const mazingSettings = config.mazing;
            if (mazingSettings) {
                url = mazingSettings?.split(';')[0];
                auth = mazingSettings?.split(';')[1];
            }
            setCurrentProvider(1);
            setIsCurrentProviderMazing(true);
        } else if (provider === TryOnProvider.Banuba) {
            const banubaSettings = config.banuba;
            if (banubaSettings) {
                url = banubaSettings?.split(';')[0];
                auth = banubaSettings?.split(';')[1];
            }
            setCurrentProvider(2);
        } else if (provider === TryOnProvider.DeepAR) {
            console.log('DeepAR' + provider);
            const deepARSettings = config.deepAR;
            if (deepARSettings) {
                url = deepARSettings?.split(';')[0];
                auth = deepARSettings?.split(';')[1];
            }
            setCurrentProvider(3);
            url = deepARIframeUrl;
            auth = 'b2a8ba67b57e4ad4de258a0a48c425602862b69ea29c993df988cb4b2f5b255ad73abd120cae6df6';
        }
        return { url: url, auth: auth };
    };

    const sendMessage = (messageToSend: any) => {
        frame.current?.contentWindow?.postMessage(messageToSend, '*');
    };

    const getAuthMessage = async () => {
        const { TryOnProvider } = await import('@zakeke/zakeke-3d-core');
        if (provider === TryOnProvider.Mazing) {
            return FormatMazingMessage('AUTH_FINE', { account_uid: providerInfoRef.current?.auth });
        } else if (provider === TryOnProvider.Banuba) {
            return FormatBanubaMessage('AUTH', { token: providerInfoRef.current?.auth });
        } else if (provider === TryOnProvider.DeepAR) {
            return FormatDeepARMessage('AUTH', { token: providerInfoRef.current?.auth });
        }
    };

    const getLoadMessage = async () => {
        const { TryOnProvider } = await import('@zakeke/zakeke-3d-core');
        const settings = { glb: model, settings: currentSettings };
        if (provider === TryOnProvider.Mazing) {
            return FormatMazingMessage('LOAD_FINE_TUNER', settings);
        } else if (provider === TryOnProvider.Banuba) {
            return FormatBanubaMessage('LOAD_FINE_TUNER', settings);
        } else if (provider === TryOnProvider.DeepAR) {
            return FormatDeepARMessage('LOAD_FINE_TUNER', settings);
        }
    };

    const sendMovementSettingsMessage = async (movement: any) => {
        const { TryOnProvider } = await import('@zakeke/zakeke-3d-core');
        let message = null;
        if (provider === TryOnProvider.Mazing) {
            message = FormatMazingMessage('FINE_TUNE_ORIENTATION', { operation: movement });
        } else if (provider === TryOnProvider.Banuba) {
            message = FormatBanubaMessage('FINE_TUNE_OPERATION', { value: { operation: movement } });
        } else if (provider === TryOnProvider.DeepAR) {
            message = FormatDeepARMessage('FINE_TUNE_OPERATION', { value: { operation: movement } });
        }
        sendMessage(message);
    };

    const sendEnvironmentSettingsMessage = async (environment: any) => {
        const { TryOnProvider } = await import('@zakeke/zakeke-3d-core');
        let message = null;
        if (provider === TryOnProvider.Mazing) {
            message = FormatMazingMessage('FINE_TUNE_ENVIRONMENT', { environment: environment });
        }
        sendMessage(message);
    };

    const sendTempleSettingsMessage = async (templeMode: any) => {
        const { TryOnProvider } = await import('@zakeke/zakeke-3d-core');
        let message = null;
        if (provider === TryOnProvider.Mazing) {
            message = FormatMazingMessage('FINE_TUNE_TEMPLE', { visible: templeMode });
        }
        sendMessage(message);
    };

    const getSettingsValue = async (message: any | undefined) => {
        const { TryOnProvider } = await import('@zakeke/zakeke-3d-core');
        if (!message) return null;
        if (provider === TryOnProvider.Mazing) {
            return message?.value?.settings ?? null;
        } else if (provider === TryOnProvider.Banuba) {
            return message?.value ?? null;
        } else if (provider === TryOnProvider.DeepAR) {
            return message?.value ?? null;
        }
        return null;
    };

    const handleMessage = async (e: MessageEvent) => {
        const { TryOnProvider } = await import('@zakeke/zakeke-3d-core');
        const message = e.data as FinetuningMessage;
        switch (message.type) {
            case FinetuningMessageType.WaitingAuthentication:
                sendMessage(await getAuthMessage());
                setIsReady(true);
                break;
            case FinetuningMessageType.AuthSuccessful:
                if (provider === TryOnProvider.Banuba) sendMessage(FormatBanubaMessage('LOAD_HANDS', null));
                onReady?.();
                sendMessage(await getLoadMessage());
                break;
            case FinetuningMessageType.FineTunerLoaded:
                onLoaded?.();
                setIsLoaded(true);
                break;
            case FinetuningMessageType.NewSettings:
                const value = await getSettingsValue(message);
                console.log('sono value  newSettings', value);
                setCurrentSettingsState(value);
                onSettingsChange?.(value);
                break;
            case FinetuningMessageType.AuthError:
                console.info('Before sent: ', await getAuthMessage());
                console.error('Auth error: ', message);
                showNotification({
                    message: T._('Authentication error', 'Admin'),
                    type: 'danger',
                    dismiss: { duration: 3000 }
                });
                break;
            case FinetuningMessageType.WebcamError:
                console.error('Webcam error: ', message);
                showNotification({
                    message: T._('Webcam error, please check your webcam', 'Admin'),
                    type: 'danger',
                    dismiss: { duration: 3000 }
                });
                break;
        }
    };

    const save = () => {
        setIsFullScreen(false);
        onFullScreen?.(false);
        onSettingsSave?.(currentSettingsState);
    };

    React.useEffect(() => {
        getProviderAuth().then((res) => {
            setProviderInfo(res);

            window.addEventListener('message', handleMessage);
        });

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, []);

    const onSettingNewDeepARSettings = (settings: any) => {
        if (settings === null)
            settings = {
                position: {
                    x: 0,
                    y: 0,
                    z: 0
                },
                rotation: {
                    x: 0,
                    y: 0,
                    z: 0
                },
                scale: 1,
                environment: 'https://zakekecdn.blob.core.windows.net/cdn/3d/assets/environments/deepAR_HDRs/Warehouse-with-lights_(intermediate)_new.hdr'
            };
        console.log('2 set settings deepAR', settings);
        setCurrentSettingsState(settings);
        onSettingsChange?.(settings);
    };

    const uploadModel = async () => {
        const { TryOnProvider } = await import('@zakeke/zakeke-3d-core');

        if (provider === TryOnProvider.DeepAR) {
            let url: string;

            if (modelUrl) {
                // DAM mode: usa l'URL originale senza caricare il file
                url = modelUrl;
            } else {
                // Non-DAM: esporta e carica il file come temp file
                const glbModel = await model.arrayBuffer();
                const file: File = new File([glbModel], 'model.glb', { type: 'model/gltf-binary' });
                url = await uploadFile(file);
            }

            setDeepARIframeUrl(config.deepAR.split(';')[0] + '?fileUrl=' + url);
            setUploadedFileUrl(url);
            onLoaded?.();
            setIsReady(true);
        }
    };

    React.useEffect(() => {
        if (deepARIframeUrl == '' && currentProvider === 3) {
            uploadModel();
        }
    }, [deepARIframeUrl]);
    console.log('providerInfo?.url', providerInfo?.url);
    return (
        <React.Fragment>
            {!isReady && <VTOFineTuningContainerOverlay $isAbove />}
            {isFullScreen && <VTOFineTuningContainerOverlay />}

            {currentProvider !== 3 ? (
                <VtoFinetuningContainerBase $isFullScreen={isFullScreen}>
                    <VtoFinetuningFrameContainer>
                        <VtoFinetuningFrame
                            ref={frame}
                            allowFullScreen={false}
                            src={currentProvider === 3 ? deepARIframeUrl || 'about:blank' : (providerInfo?.url ?? 'about:blank')}
                            allow='accelerometer; magnetometer; autoplay; encrypted-media; gyroscope; picture-in-picture; camera *; xr-spatial-tracking;'
                        />
                    </VtoFinetuningFrameContainer>

                    {isLoaded && (
                        <VtoFinetuningControlsContainer>
                            <VtoFinetuningControlsVariableContainer $heightBlock={isFullScreen ? 5 : 8}>
                                <StyledIcon
                                    onClick={() => {
                                        const finalValue = !isFullScreen;
                                        setIsFullScreen(finalValue);
                                        onFullScreen?.(finalValue);
                                    }}
                                >
                                    {isFullScreen ? <TimesSolid type='primary'></TimesSolid> : <FullscreenIcon type='primary'></FullscreenIcon>}
                                </StyledIcon>
                            </VtoFinetuningControlsVariableContainer>
                            <VtoFinetuningControlsVariableContainer $heightBlock={isFullScreen ? 50 : 50}>
                                {isCurrentProviderMazing && (
                                    <CenteredFluidContainer>
                                        <ComponentsContainer>
                                            <span>
                                                {T._('Temple mode ', 'Admin')}{' '}
                                                <HelpIcon
                                                    tooltip={T._(
                                                        'Selecting the "Hidden" mode allows you to hide the final part of the glasses1 temples arms in the VTO. This ensures a consistent appearance and prevents any possible issues with temples being incorrectly positioned behind the ears on different head shapes.',
                                                        'Admin'
                                                    )}
                                                />
                                            </span>

                                            <AdvancedSelect
                                                value={tryOnMazingFinetuningTemples.find((x) => x.value === currentSettings?.templeMode)?.value.toString()}
                                                onChange={(value) => {
                                                    sendTempleSettingsMessage(value == 'true');
                                                }}
                                            >
                                                {tryOnMazingFinetuningTemples.map((x) => (
                                                    <option value={x.value.toString()}>{x.label}</option>
                                                ))}
                                            </AdvancedSelect>
                                        </ComponentsContainer>
                                        <ComponentsContainer>
                                            <span>
                                                {T._('Effect ', 'Admin')}{' '}
                                                <HelpIcon tooltip={T._('You can select your preferred lighting style for the glasses in the VTO', 'Admin')} />
                                            </span>

                                            <AdvancedSelect
                                                value={tryOnMazingFinetuningEnvironments
                                                    .find((x) => x.value === currentSettings?.environment)
                                                    ?.value.toString()}
                                                onChange={(value) => {
                                                    sendEnvironmentSettingsMessage(value);
                                                }}
                                            >
                                                {tryOnMazingFinetuningEnvironments.map((x) => (
                                                    <option value={x.value.toString()}>{x.label}</option>
                                                ))}
                                            </AdvancedSelect>
                                        </ComponentsContainer>
                                    </CenteredFluidContainer>
                                )}
                            </VtoFinetuningControlsVariableContainer>
                            <VtoFinetuningControlsVariableContainer $heightBlock={isFullScreen ? 25 : 38}>
                                <ControlsMovementContainer>
                                    <MovementButton onClick={() => sendMovementSettingsMessage('plus')}>
                                        <PlusSolid></PlusSolid>
                                    </MovementButton>
                                    <MovementButton onClick={() => sendMovementSettingsMessage('up')}>
                                        <ArrowUpSolid></ArrowUpSolid>
                                    </MovementButton>
                                    <MovementButton onClick={() => sendMovementSettingsMessage('minus')}>
                                        <MinusSolid></MinusSolid>
                                    </MovementButton>
                                    <MovementButton onClick={() => sendMovementSettingsMessage('left')}>
                                        <ArrowLeftSolid></ArrowLeftSolid>
                                    </MovementButton>
                                    <MovementButton onClick={() => sendMovementSettingsMessage('down')}>
                                        <ArrowDownSolid></ArrowDownSolid>
                                    </MovementButton>
                                    <MovementButton onClick={() => sendMovementSettingsMessage('right')}>
                                        <ArrowRightSolid></ArrowRightSolid>
                                    </MovementButton>
                                </ControlsMovementContainer>
                            </VtoFinetuningControlsVariableContainer>
                            {isFullScreen && (
                                <VtoFinetuningControlsVariableContainer $heightBlock={20}>
                                    <PrimaryButton onClick={save}>{T._('Save', 'Admin')}</PrimaryButton>
                                </VtoFinetuningControlsVariableContainer>
                            )}
                        </VtoFinetuningControlsContainer>
                    )}
                    {children}
                </VtoFinetuningContainerBase>
            ) : (
                <>
                    {[
                        console.log('Rendering DeepAR with uploadedFileUrl:', uploadedFileUrl),
                        <DeepAR url={uploadedFileUrl} settings={currentSettingsState} onSettingNewDeepARSettings={onSettingNewDeepARSettings} />
                    ].filter((item) => item !== undefined && item !== null)}
                </>
            )}
        </React.Fragment>
    );
};

export default VtoFinetuning;
